import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import notificationManager from '../utils/notificationManager';
import { API_URL } from '../config/api';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = {};
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.notificationsEnabled = localStorage.getItem('notifications_enabled') !== 'false';
    this.soundEnabled = localStorage.getItem('sound_enabled') !== 'false';
    this.lastConnectionError = null;
    this.pingInterval = null;
    this._queueMonitorInterval = null;
  }

  // Inicializa a conexão com o servidor socket.io
  connect() {
    if (this.socket) return;

    // Usar origem da API quando disponível (em dev garante localhost:3000)
    const socketOrigin = (API_URL ? API_URL.replace(/\/?api$/, '') : null) || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');
    const socketUrl = socketOrigin;

    this.socket = io(`${socketUrl}/senhas`, {
      transports: ['websocket', 'polling'], // Tentar websocket primeiro, fallback para polling
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      timeout: 10000 // 10 segundos timeout
    });

    this.socket.on('connect', () => {
      console.log('✅ Conectado ao servidor de senhas via socket.io');
      this.connected = true;
      this.reconnectAttempts = 0;
      this.lastConnectionError = null;
      
      // Mostrar notificação apenas se houve reconexão
      if (this.reconnectAttempts > 0 && this.notificationsEnabled) {
        notificationManager.conexaoEstabelecida();
      }
      
      // Configurar ping para manter a conexão ativa
      this.startPingInterval();
      
      // Disparar evento para todos os ouvintes
      if (this.listeners.connect) {
        this.listeners.connect.forEach(callback => callback());
      }
    });

    this.socket.on('disconnect', () => {
      console.log('❌ Desconectado do servidor de senhas');
      this.connected = false;
      this.clearPingInterval();
      
      if (this.notificationsEnabled) {
        notificationManager.conexaoPerdida();
      }
      
      // Disparar evento para todos os ouvintes
      if (this.listeners.disconnect) {
        this.listeners.disconnect.forEach(callback => callback());
      }
    });

    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;
      this.lastConnectionError = error.message;
      console.error(`❌ Erro ao conectar ao servidor (tentativa ${this.reconnectAttempts}/${this.maxReconnectAttempts}):`, error);
      
      // Mostrar notificação apenas na primeira tentativa ou a cada 3 tentativas
      if (this.reconnectAttempts === 1 || this.reconnectAttempts % 3 === 0) {
        if (this.notificationsEnabled) {
          notificationManager.erroConexao(error.message, this.reconnectAttempts, this.maxReconnectAttempts);
        }
      }
      
      // Disparar evento para todos os ouvintes
      if (this.listeners.connect_error) {
        this.listeners.connect_error.forEach(callback => callback(error));
      }
    });

    // Lidar com reconexão automática
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Tentativa de reconexão ${attemptNumber}/${this.maxReconnectAttempts}`);
      
      // Disparar evento para todos os ouvintes
      if (this.listeners.reconnect_attempt) {
        this.listeners.reconnect_attempt.forEach(callback => callback(attemptNumber));
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`✅ Reconectado com sucesso após ${attemptNumber} tentativas`);
      
      // Disparar evento para todos os ouvintes de reconexão 
      if (this.listeners.reconnect) {
        this.listeners.reconnect.forEach(callback => callback(attemptNumber));
      }
      
      // Aguardar um breve momento para garantir que a conexão está estável antes de solicitar status
      setTimeout(() => {
        this.solicitarStatusChamadaSeguro().catch(error => {
          console.warn('Erro ao solicitar status após reconexão:', error);
        });
      }, 500);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Falha ao reconectar após múltiplas tentativas');
      
      if (this.notificationsEnabled) {
        notificationManager.error('Falha ao reconectar ao servidor após várias tentativas. Recarregue a página.');
      }
      
      // Disparar evento para todos os ouvintes
      if (this.listeners.reconnect_failed) {
        this.listeners.reconnect_failed.forEach(callback => callback());
      }
    });

    // ==========================================
    // EVENTOS WEBSOCKET EXISTENTES (MANTIDOS)
    // ==========================================

    // Escutar evento de teste de conexão
    this.socket.on('testeConexao', (data) => {
      console.log('🔧 Teste de conexão recebido:', data);
      
      // Responder ao servidor confirmando recebimento
      this.socket.emit('confirmarTesteConexao', {
        timestamp: new Date(),
        mensagemOriginal: data.mensagem
      });
      
      // Disparar evento para todos os ouvintes
      if (this.listeners.testeConexao) {
        this.listeners.testeConexao.forEach(callback => callback(data));
      }
    });

    // Escutar eventos de ping/pong para diagnóstico
    this.socket.on('pong', (data) => {
      console.log('🏓 Pong recebido:', data);
      
      // Disparar evento para todos os ouvintes
      if (this.listeners.pong) {
        this.listeners.pong.forEach(callback => callback(data));
      }
    });

    // Escutar eventos específicos de senhas (EVENTOS EXISTENTES)
    this.socket.on('senhaChamada', (data) => {
      console.log('📞 Senha chamada (evento legado):', data);
      if (this.listeners.senhaChamada) {
        this.listeners.senhaChamada.forEach(callback => callback(data));
      }
      
      // Som removido - conforme solicitado pelo usuário
      // if (this.soundEnabled) {
      //   this.playAlertSound();
      // }
    });

    this.socket.on('chamadaEnfileirada', (data) => {
      console.log('📋 Chamada enfileirada:', data);
      if (this.listeners.chamadaEnfileirada) {
        this.listeners.chamadaEnfileirada.forEach(callback => callback(data));
      }
      
      if (this.notificationsEnabled) {
        notificationManager.chamadaEnfileirada(data.posicaoFila);
      }
    });

    this.socket.on('chamadaIniciando', (data) => {
      console.log('🚀 Chamada iniciando:', data);
      if (this.listeners.chamadaIniciando) {
        this.listeners.chamadaIniciando.forEach(callback => callback(data));
      }
    });

    this.socket.on('chamadaConcluida', (data) => {
      console.log('✅ Chamada concluída:', data);
      if (this.listeners.chamadaConcluida) {
        this.listeners.chamadaConcluida.forEach(callback => callback(data));
      }
      
      if (this.notificationsEnabled) {
        // Extrair o número da senha corretamente do objeto data
        // O backend envia: { data: senha } onde senha é o objeto do Prisma
        const numeroSenha = data?.data?.senha || data?.senha || 'N/A';
        console.log('🔍 Dados recebidos:', data);
        console.log('🔍 Número da senha extraído para notificação:', numeroSenha);
        notificationManager.chamadaConcluida(numeroSenha);
      }
    });

    this.socket.on('chamadaErro', (data) => {
      console.log('❌ Erro na chamada:', data);
      if (this.listeners.chamadaErro) {
        this.listeners.chamadaErro.forEach(callback => callback(data));
      }
      
      if (this.notificationsEnabled) {
        toast.error(`Erro ao chamar senha: ${data.error}`);
      }
    });

    this.socket.on('statusChamadaAtualizado', (data) => {
      console.log('📊 Status da chamada atualizado:', data);
      if (this.listeners.statusChamadaAtualizado) {
        this.listeners.statusChamadaAtualizado.forEach(callback => callback(data));
      }
    });

    // ==========================================
    // EVENTOS DE FILA DE VOCALIZAÇÃO
    // ==========================================
    
    this.socket.on('senhaAguardandoNaFila', (data) => {
      console.log('🔄 Senha aguardando na fila de vocalização:', data);
      if (this.listeners.senhaAguardandoNaFila) {
        this.listeners.senhaAguardandoNaFila.forEach(callback => callback(data));
      }
    });
    
    this.socket.on('senhaRealmenteChamada', (data) => {
      console.log('🚀 Senha realmente chamada (vocalizada):', data);
      if (this.listeners.senhaRealmenteChamada) {
        this.listeners.senhaRealmenteChamada.forEach(callback => callback(data));
      }
    });

    // ==========================================
    // NOVOS EVENTOS WEBSOCKET REAL-TIME
    // ==========================================

    // 📱 CATEGORIA 1: Eventos de Senhas (substitui useSenhasApi)
    this.socket.on('senhaEmitida', (data) => {
      console.log('🆕 WebSocket: Nova senha emitida', data);
      if (this.listeners.senhaEmitida) {
        this.listeners.senhaEmitida.forEach(callback => callback(data));
      }
      
      // CORREÇÃO: Usar o gerenciador de notificações para evitar duplicações
      if (this.notificationsEnabled && data.senha) {
        notificationManager.senhaEmitida(data.senha.numero);
      }
    });

    this.socket.on('senhaStatusChanged', (data) => {
      console.log('🔄 WebSocket: Status da senha alterado', data);
      if (this.listeners.senhaStatusChanged) {
        this.listeners.senhaStatusChanged.forEach(callback => callback(data));
      }
    });

    this.socket.on('senhaFinalizada', (data) => {
      console.log('✅ WebSocket: Senha finalizada', data);
      if (this.listeners.senhaFinalizada) {
        this.listeners.senhaFinalizada.forEach(callback => callback(data));
      }
      
      if (this.notificationsEnabled && data.senha) {
        notificationManager.atendimentoFinalizado(data.senha);
      }
    });

    this.socket.on('senhaCancelada', (data) => {
      console.log('❌ WebSocket: Senha cancelada', data);
      if (this.listeners.senhaCancelada) {
        this.listeners.senhaCancelada.forEach(callback => callback(data));
      }
      
      if (this.notificationsEnabled && data.senha) {
        const motivo = data.motivo === 'NAO_COMPARECEU' ? 'não compareceu' : 'cancelada';
        notificationManager.senhaCancelada(data.senha, motivo);
      }
    });

    // 📋 CATEGORIA 2: Eventos de Fila (substitui useDashboard.buscarSenhas)
    this.socket.on('filaAtualizada', (data) => {
      console.log('📋 WebSocket: Fila atualizada', data);
      if (this.listeners.filaAtualizada) {
        this.listeners.filaAtualizada.forEach(callback => callback(data));
      }
    });

    this.socket.on('proximasSenhasChanged', (data) => {
      console.log('🎯 WebSocket: Próximas senhas alteradas', data);
      if (this.listeners.proximasSenhasChanged) {
        this.listeners.proximasSenhasChanged.forEach(callback => callback(data));
      }
    });

    // 📊 CATEGORIA 3: Eventos de Estatísticas (substitui buscarEstatisticas)
    this.socket.on('estatisticasAtualizadas', (data) => {
      console.log('📊 WebSocket: Estatísticas atualizadas', data);
      if (this.listeners.estatisticasAtualizadas) {
        this.listeners.estatisticasAtualizadas.forEach(callback => callback(data));
      }
    });

    this.socket.on('metricasTempoReal', (data) => {
      console.log('⚡ WebSocket: Métricas em tempo real', data);
      if (this.listeners.metricasTempoReal) {
        this.listeners.metricasTempoReal.forEach(callback => callback(data));
      }
    });

    // 👤 CATEGORIA 4: Eventos de Usuário (substitui consultas de usuário)
    this.socket.on('usuarioAtualizado', (data) => {
      console.log('👤 WebSocket: Usuário atualizado', data);
      if (this.listeners.usuarioAtualizado) {
        this.listeners.usuarioAtualizado.forEach(callback => callback(data));
      }
    });

    this.socket.on('sessionStatus', (data) => {
      console.log('🔗 WebSocket: Status da sessão', data);
      if (this.listeners.sessionStatus) {
        this.listeners.sessionStatus.forEach(callback => callback(data));
      }
    });

    // 📱 CATEGORIA 5: Eventos de Display/Recepção
    this.socket.on('displayAtualizado', (data) => {
      console.log('📱 WebSocket: Display atualizado', data);
      if (this.listeners.displayAtualizado) {
        this.listeners.displayAtualizado.forEach(callback => callback(data));
      }
    });

    this.socket.on('chamadaDisplay', (data) => {
      console.log('📺 WebSocket: Chamada para display', data);
      if (this.listeners.chamadaDisplay) {
        this.listeners.chamadaDisplay.forEach(callback => callback(data));
      }
      
      // Som removido - conforme solicitado pelo usuário
      // if (this.soundEnabled && data.vocalizarSenha) {
      //   this.playAlertSound();
      // }
    });

    // ==========================================
    // EVENTOS DE SISTEMA E MONITORAMENTO
    // ==========================================

    this.socket.on('systemStatus', (data) => {
      console.log('🔧 WebSocket: Status do sistema', data);
      if (this.listeners.systemStatus) {
        this.listeners.systemStatus.forEach(callback => callback(data));
      }
    });

    this.socket.on('errorMessage', (data) => {
      console.error('❌ WebSocket: Mensagem de erro', data);
      if (this.listeners.errorMessage) {
        this.listeners.errorMessage.forEach(callback => callback(data));
      }
      
      if (this.notificationsEnabled) {
        notificationManager.erroSistema(data.message || 'Erro desconhecido');
      }
    });

    // 🔄 EVENTOS DE TRANSFERÊNCIA DE SENHAS
    this.socket.on('senhaTransferida', (data) => {
      console.log('📤 WebSocket: Senha transferida recebida', data);
      if (this.listeners.senhaTransferida) {
        this.listeners.senhaTransferida.forEach(callback => callback(data));
      }
      
      // CORREÇÃO: Usar o gerenciador de notificações para evitar duplicações
      if (this.notificationsEnabled && data.senha) {
        const remetente = data.senha.transferidaDe?.nome || 'Atendente';
        const motivo = data.senha.motivo || '';
        notificationManager.senhaTransferida(data.senha.numero, remetente, motivo);
        
        // Som especial para transferências recebidas
        if (this.soundEnabled) {
          this.playTransferNotificationSound();
        }
      }
    });

    this.socket.on('transferenciaConfirmada', (data) => {
      console.log('✅ WebSocket: Transferência confirmada', data);
      if (this.listeners.transferenciaConfirmada) {
        this.listeners.transferenciaConfirmada.forEach(callback => callback(data));
      }
      
      // ✅ Notificação removida - será tratada pela camada superior para evitar duplicatas
      if (this.notificationsEnabled && data.destinatario) {
        console.log('✅ Transferência realizada com sucesso:', data.destinatario.nome);
      }
    });

    this.socket.on('erroTransferencia', (data) => {
      console.error('❌ WebSocket: Erro na transferência', data);
      if (this.listeners.erroTransferencia) {
        this.listeners.erroTransferencia.forEach(callback => callback(data));
      }
      
      if (this.notificationsEnabled) {
        toast.error(`Erro ao transferir senha: ${data.error}`);
      }
    });
  }

  // ==========================================
  // MÉTODOS AUXILIARES (MANTIDOS)
  // ==========================================

  startPingInterval() {
    this.clearPingInterval();
    this.pingInterval = setInterval(() => {
      this.ping();
    }, 30000); // 30 segundos
  }

  clearPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  ping() {
    if (this.socket && this.connected) {
      this.socket.emit('ping', { timestamp: new Date() });
    }
  }

  /*playAlertSound() {
    try {
      // Reproduzir som de alerta usando Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
      oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn('Não foi possível reproduzir som de alerta:', error);
      
      // Fallback: reproduzir beep usando HTML5 audio
      try {
        // Criar um beep usando data URL
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmMcBSuA2eu8fyEFl8H77Gw6Ci2I4eJ8LgowgtzlvHwlCLHA+M18NQgncNnmwXoqCrGA2+GBLAgvgdz...');
        audio.volume = 0.3;
        // audio.play(); // Som removido - conforme solicitado pelo usuário
      } catch (fallbackError) {
        console.warn('Fallback de áudio também falhou:', fallbackError);
      }
    }
  }*/

  /**
   * Som específico para notificações de transferência
   * Frequências diferenciadas para distinguir de outras notificações
   */
  playTransferNotificationSound() {
    try {
      // Som específico para transferências: 800Hz → 600Hz → 800Hz
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Padrão diferenciado para transferência
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.25, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Não foi possível reproduzir som de transferência:', error);
      
      // Fallback removido - som desabilitado conforme solicitado pelo usuário
      // this.playAlertSound();
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.clearPingInterval();
      this.stopQueueMonitoring();
    }
  }

  reconnect() {
    this.disconnect();
    this.connect();
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback = null) {
    if (!this.listeners[event]) return;
    
    if (callback) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    } else {
      this.listeners[event] = [];
    }
  }

  toggleNotifications(enabled) {
    this.notificationsEnabled = enabled;
    localStorage.setItem('notifications_enabled', enabled.toString());
  }

  toggleSound(enabled) {
    this.soundEnabled = enabled;
    localStorage.setItem('sound_enabled', enabled.toString());
  }

  // ==========================================
  // MÉTODOS PARA EMITIR EVENTOS (EXPANDIDOS)
  // ==========================================

  // Método original mantido para compatibilidade
  chamarSenha(id, atendenteId, guiche, iniciarAtendimento = false) {
    if (!this.socket || !this.connected) {
      toast.error('Não conectado ao servidor. Verifique sua conexão.');
      return false;
    }

    this.socket.emit('chamarSenha', {
      id,
      atendenteId,
      guiche,
      iniciarAtendimento,
      timestamp: Date.now()
    });
    
    return true;
  }

  // Novo método para emitir eventos de iniciar atendimento
  iniciarAtendimento(senhaId, atendenteId, guiche) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível iniciar atendimento.');
      return Promise.reject(new Error('Socket não conectado'));
    }

    console.log('🚀 Emitindo: iniciarAtendimento', { senhaId, atendenteId, guiche });
    this.socket.emit('iniciarAtendimento', {
      senhaId,
      atendenteId,
      guiche,
      timestamp: Date.now()
    });
  }

  // Novo método para emitir eventos de finalizar atendimento
  finalizarAtendimento(senhaId, atendenteId, observacoes = null) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível finalizar atendimento.');
      return Promise.reject(new Error('Socket não conectado'));
    }

    console.log('✅ Emitindo: finalizarAtendimento', { senhaId, atendenteId, observacoes });
    this.socket.emit('finalizarAtendimento', {
      senhaId,
      atendenteId,
      observacoes,
      timestamp: Date.now()
    });
  }

  // Novo método para emitir eventos de cancelar senha
  cancelarSenha(senhaId, atendenteId, motivo = 'NAO_COMPARECEU') {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível cancelar senha.');
      return Promise.reject(new Error('Socket não conectado'));
    }

    console.log('❌ Emitindo: cancelarSenha', { senhaId, atendenteId, motivo });
    this.socket.emit('cancelarSenha', {
      senhaId,
      atendenteId,
      motivo,
      timestamp: Date.now()
    });
  }

  // Novo método para subscrever a dados do usuário
  subscribeToUser(usuarioId) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível subscrever ao usuário.');
      return;
    }

    console.log('👤 Emitindo: subscribeToUser', { usuarioId });
    this.socket.emit('subscribeToUser', { usuarioId });
  }

  // Novo método para subscrever a serviços específicos
  subscribeToServicos(atendenteId, servicosIds) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível subscrever aos serviços.');
      return;
    }

    console.log('📋 Emitindo: subscribeToServicos', { atendenteId, servicosIds });
    this.socket.emit('subscribeToServicos', {
      atendenteId,
      servicosIds
    });
  }

  // Novo método para atualizar status do usuário
  updateUserStatus(status) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível atualizar status.');
      return;
    }

    console.log('🔗 Emitindo: updateUserStatus', { status });
    this.socket.emit('updateUserStatus', {
      status,
      timestamp: Date.now()
    });
  }

  // Método original de rechamar mantido para compatibilidade
  rechamarSenha(senhaNumero, guiche, preferencial = false) {
    if (!this.socket || !this.connected) {
      toast.error('Não conectado ao servidor. Verifique sua conexão.');
      return false;
    }

    const vocalizarSenha = this.soundEnabled;
    
    this.socket.emit('rechamarSenha', {
      senha: senhaNumero,
      guiche: guiche,
      preferencial: preferencial,
      vocalizarSenha: vocalizarSenha,
      timestamp: Date.now()
    });

    if (this.notificationsEnabled) {
      toast.info(`Rechamando senha ${senhaNumero} para o guichê ${guiche}`);
    }
    
    return true; // Retornar true quando conseguir enviar
  }

  // ==========================================
  // MÉTODOS DE DIAGNÓSTICO E CONTROLE (MANTIDOS)
  // ==========================================

  // Função utilitária para aguardar a conexão
  waitForConnection(timeout = 10000) {
    return new Promise((resolve, reject) => {
      if (this.connected && this.socket && this.socket.connected) {
        resolve();
        return;
      }

      const connectListener = () => {
        cleanup();
        resolve();
      };

      const errorListener = (error) => {
        cleanup();
        reject(new Error(`Falha na conexão: ${error.message}`));
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout aguardando conexão WebSocket'));
      }, timeout);

      const cleanup = () => {
        this.off('connect', connectListener);
        this.off('connect_error', errorListener);
        clearTimeout(timeoutId);
      };

      this.on('connect', connectListener);
      this.on('connect_error', errorListener);
    });
  }

  // Versão melhorada do solicitarStatusChamada com aguardo de conexão
  async solicitarStatusChamadaSeguro(timeout = 5000) {
    try {
      await this.waitForConnection(timeout);
      return await this.solicitarStatusChamada();
    } catch (error) {
      console.warn('Erro ao solicitar status com segurança:', error.message);
      throw error;
    }
  }

  solicitarStatusChamada() {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível solicitar status.');
      return Promise.reject(new Error('Socket não conectado'));
    }
    
    // Verificação adicional para garantir que o socket está realmente conectado
    if (!this.socket.connected) {
      console.warn('Socket não está em estado conectado. Aguardando conexão...');
      return Promise.reject(new Error('Socket não está conectado'));
    }
    
    return new Promise((resolve, reject) => {
      try {
        // Criar um listener temporário para receber a resposta
        const tempListener = (data) => {
          console.log('Status recebido:', data);
          // Remover o listener após receber a resposta
          if (this.socket) {
            this.socket.off('statusChamadaAtualizado', tempListener);
          }
          resolve(data);
        };
        
        // Registrar o listener temporário
        this.socket.on('statusChamadaAtualizado', tempListener);
        
        // Solicitar o status
        this.socket.emit('statusChamada');
        
        // Timeout para limpar o listener se não receber resposta
        setTimeout(() => {
          if (this.socket) {
            this.socket.off('statusChamadaAtualizado', tempListener);
          }
          resolve(null);
        }, 5000);
      } catch (error) {
        console.error('Erro ao solicitar status da chamada:', error);
        reject(error);
      }
    });
  }

  confirmarExibicaoSenha(senhaId) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível confirmar exibição.');
      return;
    }

    this.socket.emit('confirmarExibicao', { senhaId });
  }

  getStatus() {
    return {
      connected: this.connected,
      reconnectAttempts: this.reconnectAttempts,
      lastConnectionError: this.lastConnectionError,
      notificationsEnabled: this.notificationsEnabled,
      soundEnabled: this.soundEnabled
    };
  }

  checkQueueStatus() {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível verificar status da fila.');
      return Promise.reject(new Error('Socket não conectado'));
    }
    
    return new Promise((resolve) => {
      // Criar um listener temporário para receber a resposta
      const tempListener = (data) => {
        console.log('Status da fila recebido:', data);
        // Remover o listener após receber a resposta
        if (this.socket) {
          this.socket.off('statusChamadaAtualizado', tempListener);
        }
        resolve(data);
      };
      
      // Registrar o listener temporário
      this.socket.on('statusChamadaAtualizado', tempListener);
      
      // Solicitar o status
      this.socket.emit('statusChamada');
      
      // Timeout para limpar o listener se não receber resposta
      setTimeout(() => {
        if (this.socket) {
          this.socket.off('statusChamadaAtualizado', tempListener);
        }
        resolve(null);
      }, 5000);
    });
  }

  // Método para forçar uma limpeza da fila no backend
  async clearQueueState() {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível limpar estado da fila.');
      return Promise.reject(new Error('Socket não conectado'));
    }
    
    return new Promise((resolve) => {
      // Criar um listener temporário para receber a resposta
      const tempListener = (data) => {
        console.log('Resposta de limpeza de fila:', data);
        // Remover o listener após receber a resposta
        if (this.socket) {
          this.socket.off('queueReset', tempListener);
        }
        resolve(data);
      };
      
      // Registrar o listener temporário
      this.socket.on('queueReset', tempListener);
      
      // Solicitar o reset da fila
      this.socket.emit('resetQueueState');
      
      // Timeout para limpar o listener se não receber resposta
      setTimeout(() => {
        if (this.socket) {
          this.socket.off('queueReset', tempListener);
        }
        resolve({ success: false, error: 'Timeout ao aguardar resposta' });
      }, 5000);
    });
  }

  // Método para obter um snapshot do estado atual da fila
  async getQueueSnapshot() {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível obter snapshot da fila.');
      return Promise.reject(new Error('Socket não conectado'));
    }
    
    return new Promise((resolve) => {
      // Criar um listener temporário para receber a resposta
      const tempListener = (data) => {
        // Remover o listener após receber a resposta
        if (this.socket) {
          this.socket.off('statusChamadaAtualizado', tempListener);
        }
        resolve(data);
      };
      
      // Registrar o listener temporário
      this.socket.on('statusChamadaAtualizado', tempListener);
      
      // Solicitar o status
      this.socket.emit('statusChamada');
      
      // Timeout para limpar o listener se não receber resposta
      setTimeout(() => {
        if (this.socket) {
          this.socket.off('statusChamadaAtualizado', tempListener);
        }
        resolve({ error: 'Timeout ao aguardar resposta do servidor' });
      }, 3000);
    });
  }

  // Método para monitorar continuamente o estado da fila
  monitorQueueState(callback, interval = 5000) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível monitorar a fila.');
      return false;
    }
    
    // Parar qualquer monitoramento existente
    this.stopQueueMonitoring();
    
    // Configurar callback permanente
    this.on('statusChamadaAtualizado', callback);
    
    // Configurar intervalo de consulta
    this._queueMonitorInterval = setInterval(() => {
      if (this.connected && this.socket && this.socket.connected) {
        this.solicitarStatusChamada().catch(error => {
          console.warn('Erro no monitoramento da fila:', error);
        });
      }
    }, interval);
    
    // Solicitar status imediatamente se conectado
    if (this.connected && this.socket && this.socket.connected) {
      this.solicitarStatusChamada().catch(error => {
        console.warn('Erro ao solicitar status inicial:', error);
      });
    }
    
    return true;
  }

  // Parar o monitoramento da fila
  stopQueueMonitoring() {
    if (this._queueMonitorInterval) {
      clearInterval(this._queueMonitorInterval);
      this._queueMonitorInterval = null;
    }
  }

  // Método para diagnosticar problemas na fila
  async diagnosticarFila() {
    console.log('🔍 Iniciando diagnóstico da fila...');
    
    try {
      // Verificar estado atual do socket
      const socketStatus = this.getStatus();
      console.log('Status do socket:', socketStatus);
      
      if (!socketStatus.connected) {
        console.warn('Socket desconectado. Tentando reconectar...');
        this.reconnect();
        
        // Aguardar um momento para a reconexão
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const novoStatus = this.getStatus();
        if (!novoStatus.connected) {
          return {
            success: false,
            error: 'Falha ao reconectar ao servidor',
            socketStatus: novoStatus
          };
        }
      }
      
      // Obter snapshot da fila
      const queueSnapshot = await this.getQueueSnapshot();
      console.log('Snapshot da fila:', queueSnapshot);
      
      // Verificar se há uma chamada presa
      if (queueSnapshot.chamadaEmAndamento) {
        console.warn('Detectada chamada possivelmente travada. Solicitando reset...');
        
        // Tentar resetar o estado
        const resetResult = await this.clearQueueState();
        console.log('Resultado do reset:', resetResult);
        
        // Verificar estado após reset
        const statusPosReset = await this.getQueueSnapshot();
        console.log('Estado da fila após reset:', statusPosReset);
        
        return {
          success: true,
          diagnostico: 'Chamada travada detectada e corrigida',
          detalhes: {
            estadoAnterior: queueSnapshot,
            resetResult,
            estadoAtual: statusPosReset
          }
        };
      }
      
      return {
        success: true,
        diagnostico: 'Fila operando normalmente',
        detalhes: queueSnapshot
      };
    } catch (error) {
      console.error('Erro durante diagnóstico da fila:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido durante diagnóstico',
        stack: error.stack
      };
    }
  }

  // ==========================================
  // MÉTODOS DE TRANSFERÊNCIA DE SENHAS
  // ==========================================

  /**
   * Transfere uma senha para outro atendente via WebSocket
   * @param {number} senhaId - ID da senha a ser transferida
   * @param {number} atendenteOrigemId - ID do atendente que está transferindo
   * @param {number} atendenteDestinoId - ID do atendente que receberá a senha
   * @param {number} guiche - Número do guichê do atendente destino
   * @param {string} motivo - Motivo da transferência (opcional)
   */
  transferirSenha(senhaId, atendenteOrigemId, atendenteDestinoId, guiche, motivo = null) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível transferir senha.');
      return Promise.reject(new Error('Socket não conectado'));
    }

    // ✅ VALIDAÇÃO CRÍTICA: Verificar dados obrigatórios
    if (!senhaId || senhaId === undefined || senhaId === null) {
      const error = new Error('ID da senha é obrigatório para transferência');
      console.error('❌ Dados obrigatórios não fornecidos para transferência:', { senhaId });
      return Promise.reject(error);
    }

    if (!atendenteOrigemId || atendenteOrigemId === undefined || atendenteOrigemId === null) {
      const error = new Error('ID do atendente de origem é obrigatório para transferência');
      console.error('❌ Dados obrigatórios não fornecidos para transferência:', { atendenteOrigemId });
      return Promise.reject(error);
    }

    if (!atendenteDestinoId || atendenteDestinoId === undefined || atendenteDestinoId === null) {
      const error = new Error('ID do atendente de destino é obrigatório para transferência');
      console.error('❌ Dados obrigatórios não fornecidos para transferência:', { atendenteDestinoId });
      return Promise.reject(error);
    }

    if (!guiche || guiche === undefined || guiche === null) {
      const error = new Error('Número do guichê é obrigatório para transferência');
      console.error('❌ Dados obrigatórios não fornecidos para transferência:', { guiche });
      return Promise.reject(error);
    }

    console.log(`🔄 Transferindo senha ${senhaId} de ${atendenteOrigemId} para ${atendenteDestinoId}`);

    const payload = {
      senhaId,
      atendenteOrigemId,
      atendenteDestinoId,
      guiche,
      motivo,
      timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
      // Timeout para a operação
      const timeoutId = setTimeout(() => {
        reject(new Error('Timeout ao transferir senha via WebSocket'));
      }, 10000);

      // Listener para resposta direta da transferência
      const realizadaListener = (data) => {
        clearTimeout(timeoutId);
        if (this.socket) {
          this.socket.off('transferenciaRealizada', realizadaListener);
          this.socket.off('erroTransferencia', erroListener);
        }
        console.log('✅ Transferência realizada:', data);
        resolve(data);
      };

      // Listener para erro de transferência
      const erroListener = (data) => {
        clearTimeout(timeoutId);
        if (this.socket) {
          this.socket.off('transferenciaRealizada', realizadaListener);
          this.socket.off('erroTransferencia', erroListener);
        }
        console.error('❌ Erro na transferência:', data);
        reject(new Error(data.error || 'Erro desconhecido na transferência'));
      };

      // Registrar listeners temporários
      this.socket.on('transferenciaRealizada', realizadaListener);
      this.socket.on('erroTransferencia', erroListener);

      // Emitir evento de transferência
      this.socket.emit('transferirSenha', payload);
    });
  }

  /**
   * Verifica se a conexão WebSocket está disponível
   */
  isConnected() {
    return this.socket && this.connected;
  }

  /**
   * Fallback HTTP para transferência de senha quando WebSocket falha
   * @param {number} senhaId - ID da senha a ser transferida
   * @param {number} atendenteOrigemId - ID do atendente que está transferindo
   * @param {number} atendenteDestinoId - ID do atendente que receberá a senha
   * @param {number} guiche - Número do guichê do atendente destino
   * @param {string} motivo - Motivo da transferência (opcional)
   */
  async transferirSenhaHTTP(senhaId, atendenteOrigemId, atendenteDestinoId, guiche, motivo = null) {
    console.log(`🔄 Fallback HTTP: Transferindo senha ${senhaId}`);

    // ✅ VALIDAÇÃO CRÍTICA: Verificar dados obrigatórios
    if (!senhaId || senhaId === undefined || senhaId === null) {
      const error = new Error('ID da senha é obrigatório para transferência HTTP');
      console.error('❌ Dados obrigatórios não fornecidos para transferência HTTP:', { senhaId });
      throw error;
    }

    if (!atendenteOrigemId || atendenteOrigemId === undefined || atendenteOrigemId === null) {
      const error = new Error('ID do atendente de origem é obrigatório para transferência HTTP');
      console.error('❌ Dados obrigatórios não fornecidos para transferência HTTP:', { atendenteOrigemId });
      throw error;
    }

    if (!atendenteDestinoId || atendenteDestinoId === undefined || atendenteDestinoId === null) {
      const error = new Error('ID do atendente de destino é obrigatório para transferência HTTP');
      console.error('❌ Dados obrigatórios não fornecidos para transferência HTTP:', { atendenteDestinoId });
      throw error;
    }

    if (!guiche || guiche === undefined || guiche === null) {
      const error = new Error('Número do guichê é obrigatório para transferência HTTP');
      console.error('❌ Dados obrigatórios não fornecidos para transferência HTTP:', { guiche });
      throw error;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    const endpoint = `${apiUrl}/senhas/${senhaId}/transferir`;

    const payload = {
      senhaId,
      atendenteOrigemId,
      atendenteDestinoId,
      guiche,
      motivo
    };

    try {
      // Buscar token de diferentes formas possíveis (chave correta é @SenhasApp:token)
      const token = localStorage.getItem('@SenhasApp:token') || 
                   localStorage.getItem('token') || 
                   localStorage.getItem('authToken') || 
                   sessionStorage.getItem('@SenhasApp:token') ||
                   sessionStorage.getItem('token') ||
                   sessionStorage.getItem('authToken');

      const headers = {
        'Content-Type': 'application/json'
      };

      // Adicionar Authorization header apenas se token existir
      if (token && token.trim() !== '') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log('🔐 Enviando requisição HTTP com headers:', { ...headers, Authorization: headers.Authorization ? 'Bearer [TOKEN]' : 'Nenhum token' });
      console.log('📦 Payload da transferência HTTP:', payload);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include' // Incluir cookies se necessário
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido no servidor' }));
        console.error('❌ Resposta de erro do servidor:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Transferência HTTP bem-sucedida:', result);
      return result;

    } catch (error) {
      console.error('❌ Erro na transferência HTTP:', error);
      throw error;
    }
  }

  /**
   * Método principal para transferir senha com fallback automático
   * @param {number} senhaId - ID da senha a ser transferida
   * @param {number} atendenteOrigemId - ID do atendente que está transferindo
   * @param {number} atendenteDestinoId - ID do atendente que receberá a senha
   * @param {number} guiche - Número do guichê do atendente destino
   * @param {string} motivo - Motivo da transferência (opcional)
   */
  async transferirSenhaComFallback(senhaId, atendenteOrigemId, atendenteDestinoId, guiche, motivo = null) {
    // ✅ VALIDAÇÃO CRÍTICA ANTES DE TUDO: Verificar dados obrigatórios
    console.log('🔍 Validando parâmetros de transferência:', {
      senhaId,
      atendenteOrigemId,
      atendenteDestinoId,
      guiche,
      motivo
    });

    if (!senhaId || senhaId === undefined || senhaId === null) {
      const error = new Error('ID da senha é obrigatório para transferência');
      console.error('❌ Dados obrigatórios não fornecidos para transferência:', { senhaId });
      throw error;
    }

    if (!atendenteOrigemId || atendenteOrigemId === undefined || atendenteOrigemId === null) {
      const error = new Error('ID do atendente de origem é obrigatório para transferência');
      console.error('❌ Dados obrigatórios não fornecidos para transferência:', { atendenteOrigemId });
      throw error;
    }

    if (!atendenteDestinoId || atendenteDestinoId === undefined || atendenteDestinoId === null) {
      const error = new Error('ID do atendente de destino é obrigatório para transferência');
      console.error('❌ Dados obrigatórios não fornecidos para transferência:', { atendenteDestinoId });
      throw error;
    }

    // Tentar WebSocket primeiro
    if (this.isConnected()) {
      try {
        return await this.transferirSenha(senhaId, atendenteOrigemId, atendenteDestinoId, guiche, motivo);
      } catch (error) {
        console.warn('Falha no WebSocket, tentando fallback HTTP:', error.message);
        
        if (this.notificationsEnabled) {
          toast.warn('Conexão instável. Usando método alternativo...');
        }
      }
    } else {
      console.warn('❌ WebSocket não conectado, usando fallback HTTP direto');
    }

    // Fallback para HTTP
    return await this.transferirSenhaHTTP(senhaId, atendenteOrigemId, atendenteDestinoId, guiche, motivo);
  }

  // ==========================================
  // BB-130-CB.6 - MÉTODO CALLBACK VOCALIZAÇÃO
  // ==========================================

  /**
   * Notifica o backend que a vocalização foi concluída
   * @param {number} senhaId - ID da senha que foi vocalizada
   * @param {boolean} sucesso - Se a vocalização foi bem-sucedida
   * @param {number} tempoVocalizacao - Tempo em milissegundos que durou a vocalização
   */
  notificarVocalizacaoConcluida(senhaId, sucesso = true, tempoVocalizacao = 0) {
    if (!this.socket || !this.connected) {
      console.warn('Socket não conectado. Impossível notificar conclusão da vocalização');
      return false;
    }

    const payload = {
      senhaId,
      sucesso,
      tempoVocalizacao,
      timestamp: Date.now()
    };

    console.log(`🔊 [CALLBACK] Notificando conclusão da vocalização para senha ${senhaId}:`, payload);
    this.socket.emit('vocalizacao-concluida', payload);
    return true;
  }

  // ==========================================
  // MÉTODOS DE CONVENIÊNCIA PARA O HOOK CENTRAL
  // ==========================================

  // Método simplificado para emitir qualquer evento
  emit(event, data) {
    if (!this.socket || !this.connected) {
      console.warn(`Socket não conectado. Impossível emitir evento: ${event}`);
      return false;
    }

    console.log(`📡 Emitindo evento: ${event}`, data);
    this.socket.emit(event, data);
    return true;
  }

  // Método para verificar se um evento específico tem listeners
  hasListeners(event) {
    return this.listeners[event] && this.listeners[event].length > 0;
  }

  // Método para limpar todos os listeners de um evento
  clearListeners(event) {
    if (this.listeners[event]) {
      this.listeners[event] = [];
    }
  }

  // Método para obter estatísticas de uso
  getUsageStats() {
    const eventCounts = {};
    Object.keys(this.listeners).forEach(event => {
      eventCounts[event] = this.listeners[event] ? this.listeners[event].length : 0;
    });

    return {
      connected: this.connected,
      totalEvents: Object.keys(this.listeners).length,
      eventCounts,
      reconnectAttempts: this.reconnectAttempts,
      lastConnectionError: this.lastConnectionError
    };
  }
}

// Singleton para ser usado em toda a aplicação
const socketService = new SocketService();
export default socketService; 